import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Globe, Code, Palette, ShoppingCart, Smartphone, Search, 
  Shield, Zap, Clock, Check, ArrowRight, Sparkles, Loader2, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const features = [
  {
    icon: Palette,
    title: "Design Sur Mesure",
    description: "Conception unique adaptée à votre identité visuelle et vos besoins"
  },
  {
    icon: Smartphone,
    title: "100% Responsive",
    description: "Votre site s'adapte parfaitement à tous les écrans"
  },
  {
    icon: Search,
    title: "SEO Optimisé",
    description: "Référencement naturel pour une visibilité maximale"
  },
  {
    icon: ShoppingCart,
    title: "E-Commerce",
    description: "Boutique en ligne avec paiement sécurisé intégré"
  },
  {
    icon: Shield,
    title: "Sécurité SSL",
    description: "Protection des données et certificat HTTPS inclus"
  },
  {
    icon: Zap,
    title: "Performance",
    description: "Temps de chargement ultra-rapide optimisé"
  }
];

const packages = [
  {
    name: "Starter",
    price: "990",
    description: "Idéal pour les freelances et petites structures",
    features: [
      "Site vitrine 5 pages",
      "Design responsive",
      "Formulaire de contact",
      "Intégration réseaux sociaux",
      "Hébergement 1 an",
      "Support email"
    ],
    popular: false,
    color: "bg-slate-100"
  },
  {
    name: "Business",
    price: "2490",
    description: "Pour les PME et entreprises en croissance",
    features: [
      "Site jusqu'à 15 pages",
      "Design sur mesure",
      "Blog intégré",
      "SEO avancé",
      "Espace admin (CMS)",
      "Analytics",
      "Hébergement 2 ans",
      "Support prioritaire"
    ],
    popular: true,
    color: "bg-primary"
  },
  {
    name: "E-Commerce",
    price: "4990",
    description: "Boutique en ligne complète",
    features: [
      "Catalogue illimité",
      "Paiement Stripe/PayPal",
      "Gestion stocks",
      "Multi-devises",
      "Emails automatiques",
      "Tableau de bord ventes",
      "Hébergement 3 ans",
      "Support 24/7"
    ],
    popular: false,
    color: "bg-slate-100"
  }
];

const processSteps = [
  { step: 1, title: "Découverte", description: "Nous analysons vos besoins et objectifs" },
  { step: 2, title: "Design", description: "Création de maquettes personnalisées" },
  { step: 3, title: "Développement", description: "Codage et intégration des fonctionnalités" },
  { step: 4, title: "Lancement", description: "Tests, formation et mise en ligne" }
];

export const SiteWeb = () => {
  const { t } = useLanguage();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingPackage, setPayingPackage] = useState(null);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${API}/agents`, { params: { branch: 'siteweb' } });
        setAgents(response.data);
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
      setLoading(false);
    };
    fetchAgents();
  }, []);

  const handlePayment = async (packageId) => {
    setPayingPackage(packageId);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/checkout`, {
        package_id: packageId,
        origin_url: originUrl
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Erreur lors de l\'initialisation du paiement');
      setPayingPackage(null);
    }
  };

  return (
    <div className="min-h-screen pt-20" data-testid="siteweb-page">
      {/* Hero Section */}
      <section className="hero-gradient py-20 md:py-32 relative overflow-hidden">
        <div className="blue-glow absolute inset-0 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm border border-slate-200 rounded-full px-4 py-2 text-sm">
                <Sparkles className="w-4 h-4 text-amber-500" />
                <span className="text-slate-600">Création Web Sur Mesure</span>
              </div>
              
              <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight">
                Votre Site Web <span className="text-primary">Professionnel</span>
              </h1>
              
              <p className="text-lg text-slate-600 max-w-xl leading-relaxed">
                Nous créons des sites web sur mesure avec toutes les fonctionnalités dont vous avez besoin. 
                Design unique, performance optimale, et accompagnement personnalisé.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/devis-simulateur">
                  <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6 text-lg hover-lift shadow-lg" data-testid="siteweb-cta-btn">
                    Établissez votre devis par simulation
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/appointment">
                  <Button variant="outline" className="rounded-full px-8 py-6 text-lg border-2">
                    Prendre rendez-vous
                  </Button>
                </Link>
              </div>
            </div>

            {/* Illustration */}
            <div className="relative hidden lg:flex justify-center items-center">
              <div className="w-80 h-80 bg-primary/10 rounded-full absolute animate-pulse" />
              <div className="relative z-10 bg-white rounded-2xl shadow-2xl p-8 animate-float">
                <div className="w-64 h-48 bg-slate-100 rounded-lg flex flex-col items-center justify-center">
                  <Globe className="w-16 h-16 text-primary mb-4" />
                  <div className="flex gap-2">
                    <div className="w-8 h-2 bg-primary rounded-full" />
                    <div className="w-12 h-2 bg-primary/50 rounded-full" />
                    <div className="w-6 h-2 bg-primary/30 rounded-full" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Fonctionnalités Incluses
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Tout ce dont vous avez besoin pour réussir en ligne
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="flex items-start gap-4 p-6 rounded-2xl bg-slate-50 hover:bg-white hover:shadow-lg transition-all animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg text-slate-900">{feature.title}</h3>
                  <p className="text-slate-600 text-sm mt-1">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Notre Processus
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              4 étapes pour créer votre site web parfait
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={step.step} className="text-center relative">
                <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4 shadow-lg">
                  {step.step}
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900 mb-2">{step.title}</h3>
                <p className="text-slate-600 text-sm">{step.description}</p>
                
                {index < processSteps.length - 1 && (
                  <div className="hidden md:block absolute top-8 left-[60%] w-[80%] h-0.5 bg-primary/20" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white" data-testid="siteweb-pricing">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 mb-4">
              Nos Offres
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Des forfaits clés en main, prêts à payer directement. Pour un budget sur-mesure poste par poste,{' '}
              <Link to="/devis-simulateur" className="text-primary underline hover:no-underline">
                utilisez le simulateur de devis
              </Link>.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {packages.map((pkg, index) => {
              const packageId = pkg.name.toLowerCase();
              return (
              <Card 
                key={pkg.name}
                className={`relative overflow-hidden card-hover animate-fade-in ${
                  pkg.popular ? 'ring-2 ring-primary shadow-xl' : ''
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
                data-testid={`package-${pkg.name.toLowerCase()}`}
              >
                {pkg.popular && (
                  <div className="absolute top-4 right-4 bg-amber-500 text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Populaire
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="font-heading font-bold text-2xl text-slate-900 mb-2">{pkg.name}</h3>
                  <p className="text-slate-600 text-sm mb-4">{pkg.description}</p>
                  
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-primary">{pkg.price}€</span>
                    <span className="text-slate-500"> HT</span>
                  </div>

                  <ul className="space-y-3 mb-8">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-3">
                    <Button 
                      onClick={() => handlePayment(packageId)}
                      disabled={payingPackage === packageId}
                      className={`w-full rounded-full gap-2 ${
                        pkg.popular 
                          ? 'bg-primary hover:bg-primary-hover text-white' 
                          : 'bg-slate-900 hover:bg-slate-800 text-white'
                      }`}
                      data-testid={`package-pay-${pkg.name.toLowerCase()}`}
                    >
                      {payingPackage === packageId ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <CreditCard className="w-4 h-4" />
                          Payer {pkg.price}€
                        </>
                      )}
                    </Button>
                    <Link to="/devis-simulateur" className="block">
                      <Button
                        variant="outline"
                        className="w-full rounded-full"
                        data-testid={`package-cta-${pkg.name.toLowerCase()}`}
                      >
                        Personnaliser avec le simulateur
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-72 h-72 bg-white rounded-full translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6">
            Prêt à créer votre site web ?
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl mx-auto">
            Obtenez un devis gratuit et personnalisé en moins de 24h
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/devis-simulateur">
              <Button className="bg-white text-primary hover:bg-slate-100 rounded-full px-8 py-6 text-lg hover-lift" data-testid="siteweb-register-btn">
                {t('getStarted')}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/appointment">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg">
                Appel gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
